/**
 * Phase 20 — Guided estimate wizard: step model.
 *
 * Platform-agnostic (no web/React-Native imports) so web and mobile
 * share one definition of the five steps and their order. The wizard is
 * a presentation shell over the existing Stella chat — these steps map
 * one-to-one onto capabilities that already ship (Phase 17 intent,
 * Phase 19 customer-described dimensions, the material picker, the
 * photos picker, and the Phase 17 quote card).
 */

export type WizardStepId =
  | "intent"
  | "project_type"
  | "dimensions"
  | "finish"
  | "photos"
  | "estimate";

export interface WizardStep {
  id: WizardStepId;
  /** Header question shown for this step. */
  title: string;
  /** Only `photos` is optional (skippable without blocking the estimate). */
  optional: boolean;
}

/** The five steps every estimate walks through. */
export const STEPS: readonly WizardStep[] = [
  { id: "intent", title: "What are we remodeling?", optional: false },
  { id: "dimensions", title: "How big is the space?", optional: false },
  { id: "finish", title: "Choose your finish", optional: false },
  { id: "photos", title: "Add a photo", optional: true },
  { id: "estimate", title: "Your estimate", optional: false },
];

export const STEP_IDS: readonly WizardStepId[] = STEPS.map((s) => s.id);

/**
 * Phase 21 — the bathroom project-type step.
 *
 * Inserted immediately AFTER `intent` and before `dimensions`, and the
 * order matters for a real reason: the chosen package determines whether
 * a measurement is needed at all (a half bath has no per-sqft items), so
 * asking project type first lets the flow skip measurement honestly
 * instead of demanding a number it will not use.
 */
export const PROJECT_TYPE_STEP: WizardStep = {
  id: "project_type",
  title: "What kind of project?",
  optional: false,
};

export interface BuildStepsArgs {
  /**
   * The tenant has ≥1 active package matching this estimate's room type.
   * False (a kitchen, or identity not captured yet) keeps the original
   * five-step flow with no empty step shown.
   */
  hasProjectTypes?: boolean;
}

/**
 * The ordered steps for one estimate's context.
 *
 * `STEPS` remains the no-packages default so existing importers are
 * unaffected; anything navigating the flow should read the list this
 * returns rather than the module constant.
 */
export function buildSteps({
  hasProjectTypes = false,
}: BuildStepsArgs = {}): readonly WizardStep[] {
  if (!hasProjectTypes) return STEPS;
  const out: WizardStep[] = [];
  for (const step of STEPS) {
    out.push(step);
    if (step.id === "intent") out.push(PROJECT_TYPE_STEP);
  }
  return out;
}

/** Step ids for one estimate's context, in order. */
export function buildStepIds(
  args: BuildStepsArgs = {},
): readonly WizardStepId[] {
  return buildSteps(args).map((s) => s.id);
}

/**
 * Position of a step in the ordered flow (0-based).
 *
 * Defaults to the five-step flow for back-compat; pass the context's
 * step ids when the project-type step may be present.
 */
export function stepIndex(
  id: WizardStepId,
  ids: readonly WizardStepId[] = STEP_IDS,
): number {
  return ids.indexOf(id);
}

/**
 * True when an intent-picker card is the free-form "Other / Something else"
 * catch-all (a tenant-configured `RemodelIntent`). Selecting it shouldn't
 * fire the normal "I want help with my <name>" turn — instead the wizard
 * hands off to Stella so the customer can describe the project. Heuristic
 * by design (the catch-all has no structured flag): matches the id or the
 * display name.
 */
export function isOtherIntent(intentId: string, displayName: string): boolean {
  const name = displayName.trim().toLowerCase();
  const id = intentId.toLowerCase();
  return (
    name === "other" ||
    name.includes("something else") ||
    name.includes("not sure") ||
    name.includes("not listed") ||
    id.includes("other") ||
    id.includes("else")
  );
}
